/**
 * API Tokens service - handles API token management
 */

import { apiClient } from './client';

// Types
export interface APIToken {
  id: string;
  name: string;
  tokenPrefix: string;
  scopes: string | null;
  expiresAt: string | null;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  createdAt: string;
  isExpired: boolean;
}

export interface APITokenCreated extends APIToken {
  token: string; // Only available at creation time
}

export interface CreateAPITokenRequest {
  name: string;
  scopes?: string | null;
  expiresInDays?: number | null;
}

export interface APITokenListResponse {
  items: APIToken[];
  total: number;
}

/**
 * API Tokens API methods
 */
export const apiTokensApi = {
  /**
   * Create a new API token
   */
  async create(data: CreateAPITokenRequest): Promise<APITokenCreated> {
    const response = await apiClient.post<any>('/api-tokens', {
      name: data.name,
      scopes: data.scopes,
      expires_in_days: data.expiresInDays,
    });
    return transformAPITokenCreated(response.data);
  },

  /**
   * List all API tokens for the current user
   */
  async list(): Promise<APITokenListResponse> {
    const response = await apiClient.get<any>('/api-tokens');
    return {
      items: response.data.items.map(transformAPIToken),
      total: response.data.total,
    };
  },

  /**
   * Get a specific API token by ID
   */
  async get(tokenId: string): Promise<APIToken> {
    const response = await apiClient.get<any>(`/api-tokens/${tokenId}`);
    return transformAPIToken(response.data);
  },

  /**
   * Revoke (delete) an API token
   */
  async revoke(tokenId: string): Promise<void> {
    await apiClient.delete(`/api-tokens/${tokenId}`);
  },
};

/**
 * Transform API response to camelCase
 * Note: Backend uses alias_generator=to_camel, so API responses are camelCase
 */
function transformAPIToken(data: any): APIToken {
  return {
    id: data.id,
    name: data.name,
    tokenPrefix: data.tokenPrefix ?? data.token_prefix,
    scopes: data.scopes,
    expiresAt: data.expiresAt ?? data.expires_at,
    lastUsedAt: data.lastUsedAt ?? data.last_used_at,
    lastUsedIp: data.lastUsedIp ?? data.last_used_ip,
    createdAt: data.createdAt ?? data.created_at,
    isExpired: data.isExpired ?? data.is_expired,
  };
}

function transformAPITokenCreated(data: any): APITokenCreated {
  return {
    ...transformAPIToken(data),
    token: data.token,
  };
}

export default apiTokensApi;
